import { Component, ElementRef, OnDestroy, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SUBSCRIPTION_MODULE_CONFIG } from '../../../subscription/subscription.token';

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

@Component({
  selector: 'app-turnstile',
  template: '<div #container></div>',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TurnstileComponent,
      multi: true
    }
  ]
})
export class TurnstileComponent implements ControlValueAccessor, OnDestroy {
  private moduleConfig = inject(SUBSCRIPTION_MODULE_CONFIG);
  private elementRef = inject(ElementRef);
  private widgetId?: string;
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit() {
    if (window.turnstile) {
      this.renderTurnstile();
    } else {
      // Load Turnstile script if not already loaded
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => this.renderTurnstile();
      document.head.appendChild(script);
    }
  }

  private renderTurnstile() {
    const container = this.elementRef.nativeElement.querySelector('div');
    this.widgetId = window.turnstile.render(container, {
      sitekey: this.moduleConfig.auth.captcha.turnstileSiteKey,
      theme: 'light',
      size: 'flexible',
      callback: (token: string) => {
        this.onChange(token);
        this.onTouched();
      },
      'expired-callback': () => {
        this.onChange(null);
        this.onTouched();
      },
      'error-callback': () => {
        this.onChange(null);
        this.onTouched();
      }
    });
  }

  writeValue(value: string): void {
    // Not needed as Turnstile manages its own state
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnDestroy() {
    if (this.widgetId && window.turnstile) {
      window.turnstile.reset(this.widgetId);
    }
  }
}
